/**
 * Configuración del Feature de Autenticación
 *
 * Este archivo centraliza toda la configuración relacionada con autenticación
 * siguiendo el principio de Single Responsibility
 */

// Definición de recursos disponibles en la aplicación
export const RESOURCES = {
  HOME: 'home',
  ACCOUNTS: 'accounts',
  AWS: 'aws',
  AWS_REPORTS: 'aws/reports',
  AWS_CREATE_ACCOUNT: 'aws/create-account',
  AWS_ACCOUNTS_REPORTS: 'aws/reports/accounts',
  AWS_EC2_REPORTS: 'aws/reports/ec2',
  AWS_S3_REPORTS: 'aws/reports/s3',
  AWS_RDS_REPORTS: 'aws/reports/rds',
  AWS_EFS_REPORTS: 'aws/reports/efs',
  AWS_IAM_REPORTS: 'aws/reports/iam',
  AWS_AD_REPORTS: 'aws/reports/ad',
  AWS_COSTS_REPORTS: 'aws/reports/monthly-costs',
  AWS_HEALTH_REPORTS:'aws/reports/aws-health-events',
  AWS_TA_REPORTS: 'aws/reports/trusted-advisor-organizational',
  AWS_CO_REPORTS:'aws/reports/compute-optimizer',
  AWS_KPI_REPORTS: 'aws/reports/kpi',
  AWS_CI_REPORTS: 'aws/reports/cost-intelligence',
  AWS_LAMBDA_REPORTS: 'aws/reports/lambda',
  AWS_DATADOG_CONSOLE_REPORTS: 'aws/reports/datadog-aws-console',
  AWS_COMPLIANCE_REPORTS: 'aws/reports/it-compliance',
  AWS_NETWORKING_REPORTS: 'aws/reports/networking',
  AWS_EKS_REPORTS:'aws/reports/eks',
  AWS_BEDROCK_AGENTS_REPORTS:'aws/reports/bedrock',
  AWS_DEVOPS_AGENT_REPORTS: 'aws/reports/devops-agent',
  AWS_SAGEMAKER_REPORTS: 'aws/reports/sagemaker',
  AWS_QUICK_REPORTS: 'aws/reports/quick',
  AWS_KIRO_REPORTS: 'aws/reports/kiro',
  AWS_COWORK_REPORTS: 'aws/reports/claude-cowork-bedrock',
  AWS_USER_MGT:'aws/user-management',
  AWS_PERMISSION_SETS: 'aws/permission-sets',
  AWS_AGENTS: 'aws/agents',
  AWS_AGENTS_HEALTHOPS: 'aws/agents/healthops',
  AZURE: 'azure',
  AZURE_REPORTS: 'azure/reports',
  AZURE_CREATE_ACCOUNT: 'azure/create-account',
  GCP: 'gcp',
  GCP_REPORTS: 'gcp/reports',
  GCP_DATADOG_REPORTS: 'gcp/reports/datadog-gcp',
  GCP_CREATE_ACCOUNT: 'gcp/create-account',
  OCI: 'oci',
  OCI_REPORTS: 'oci/reports',
  OCI_CREATE_ACCOUNT: 'oci/create-account',
  EMAIL: 'email',
  EMAIL_BULK: 'email/bulk',
  WIZ: 'wiz',
  WIZ_REPORTS: 'wiz/reports',
  // AWS LCCS (OutpostForge) capacity management module. A single resource
  // gates the whole module (capacity management, tasks, sandbox, registry):
  // only members of the CES_LCCS_SLOTTING Cognito group get access.
  LCCS: 'outposts',
};

// Definición de acciones disponibles
export const ACTIONS = {
  VIEW: 'view',
  CREATE: 'create',
  UPDATE: 'update',
  DELETE: 'delete',
  MANAGE: 'manage',
};

// Configuración de permisos por rol usando recursos y acciones
export const ROLE_PERMISSIONS = {
  CES_ADMIN: {
    resources: [
      RESOURCES.HOME,
      RESOURCES.ACCOUNTS,
      RESOURCES.AWS,
      RESOURCES.AWS_REPORTS,
      RESOURCES.AWS_ACCOUNTS_REPORTS,
      RESOURCES.AWS_EC2_REPORTS,
      RESOURCES.AWS_S3_REPORTS,
      RESOURCES.AWS_RDS_REPORTS,
      RESOURCES.AWS_EFS_REPORTS,
      RESOURCES.AWS_IAM_REPORTS,
      RESOURCES.AWS_AD_REPORTS,
      RESOURCES.AWS_COSTS_REPORTS,
      RESOURCES.AWS_HEALTH_REPORTS,
      RESOURCES.AWS_TA_REPORTS,
      RESOURCES.AWS_CO_REPORTS,
      RESOURCES.AWS_KPI_REPORTS,
      RESOURCES.AWS_CI_REPORTS,
      RESOURCES.AWS_DATADOG_CONSOLE_REPORTS,
      RESOURCES.AWS_LAMBDA_REPORTS,
      RESOURCES.AWS_COMPLIANCE_REPORTS,
      RESOURCES.AWS_NETWORKING_REPORTS,
      RESOURCES.AWS_EKS_REPORTS,
      RESOURCES.AWS_BEDROCK_AGENTS_REPORTS,
      RESOURCES.AWS_DEVOPS_AGENT_REPORTS,
      RESOURCES.AWS_SAGEMAKER_REPORTS,
      RESOURCES.AWS_QUICK_REPORTS,
      RESOURCES.AWS_KIRO_REPORTS,
      RESOURCES.AWS_COWORK_REPORTS,
      RESOURCES.AWS_AGENTS,
      RESOURCES.AWS_CREATE_ACCOUNT,
      RESOURCES.AWS_USER_MGT,
      RESOURCES.AWS_PERMISSION_SETS,
      RESOURCES.AWS_AGENTS_HEALTHOPS,
      RESOURCES.AZURE,
      RESOURCES.AZURE_REPORTS,
      RESOURCES.AZURE_CREATE_ACCOUNT,
      RESOURCES.GCP,
      RESOURCES.GCP_REPORTS,
      RESOURCES.GCP_DATADOG_REPORTS,
      RESOURCES.GCP_CREATE_ACCOUNT,
      RESOURCES.OCI,
      RESOURCES.OCI_REPORTS,
      RESOURCES.OCI_CREATE_ACCOUNT,
      RESOURCES.EMAIL,
      RESOURCES.EMAIL_BULK,
      RESOURCES.WIZ,
      RESOURCES.WIZ_REPORTS,
    ],
    actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.UPDATE, ACTIONS.DELETE, ACTIONS.MANAGE],
  },
  CES_MANAGER: {
    resources: [
      RESOURCES.HOME,
      RESOURCES.ACCOUNTS,
      RESOURCES.AWS,
      RESOURCES.AWS_REPORTS,
      RESOURCES.AWS_ACCOUNTS_REPORTS,
      RESOURCES.AWS_EC2_REPORTS,
      RESOURCES.AWS_S3_REPORTS,
      RESOURCES.AWS_RDS_REPORTS,
      RESOURCES.AWS_EFS_REPORTS,
      RESOURCES.AWS_IAM_REPORTS,
      RESOURCES.AWS_AD_REPORTS,
      RESOURCES.AWS_COSTS_REPORTS,
      RESOURCES.AWS_HEALTH_REPORTS,
      RESOURCES.AWS_TA_REPORTS,
      RESOURCES.AWS_CO_REPORTS,
      RESOURCES.AWS_KPI_REPORTS,
      RESOURCES.AWS_CI_REPORTS,
      RESOURCES.AWS_DATADOG_CONSOLE_REPORTS,
      RESOURCES.AWS_LAMBDA_REPORTS,
      RESOURCES.AWS_COMPLIANCE_REPORTS,
      RESOURCES.AWS_NETWORKING_REPORTS,
      RESOURCES.AWS_EKS_REPORTS,
      RESOURCES.AWS_BEDROCK_AGENTS_REPORTS,
      RESOURCES.AWS_DEVOPS_AGENT_REPORTS,
      RESOURCES.AWS_SAGEMAKER_REPORTS,
      RESOURCES.AWS_QUICK_REPORTS,
      RESOURCES.AWS_KIRO_REPORTS,
      RESOURCES.AWS_COWORK_REPORTS,
      RESOURCES.AWS_AGENTS,
      RESOURCES.AWS_AGENTS_HEALTHOPS,
      RESOURCES.AWS_CREATE_ACCOUNT,
      RESOURCES.AZURE,
      RESOURCES.AZURE_REPORTS,
      RESOURCES.GCP,
      RESOURCES.GCP_REPORTS,
      RESOURCES.GCP_DATADOG_REPORTS,
      RESOURCES.OCI,
      RESOURCES.OCI_REPORTS,
      RESOURCES.EMAIL,
      RESOURCES.EMAIL_BULK,
    ],
    actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.UPDATE],
  },
  CES_USER: {
    resources: [
      RESOURCES.HOME,
      RESOURCES.ACCOUNTS,
      RESOURCES.AWS,
      RESOURCES.AWS_REPORTS,
      RESOURCES.AWS_ACCOUNTS_REPORTS,
      RESOURCES.AWS_EC2_REPORTS,
      RESOURCES.AWS_S3_REPORTS,
      RESOURCES.AWS_RDS_REPORTS,
      RESOURCES.AWS_EFS_REPORTS,
      RESOURCES.AWS_IAM_REPORTS,
      RESOURCES.AWS_AD_REPORTS,
      RESOURCES.AWS_COSTS_REPORTS,
      RESOURCES.AWS_HEALTH_REPORTS,
      RESOURCES.AWS_TA_REPORTS,
      RESOURCES.AWS_CO_REPORTS,
      RESOURCES.AWS_KPI_REPORTS,
      RESOURCES.AWS_CI_REPORTS,
      RESOURCES.AWS_DATADOG_CONSOLE_REPORTS,
      RESOURCES.AWS_LAMBDA_REPORTS,
      RESOURCES.AWS_COMPLIANCE_REPORTS,
      RESOURCES.AWS_NETWORKING_REPORTS,
      RESOURCES.AWS_EKS_REPORTS,
      RESOURCES.AWS_BEDROCK_AGENTS_REPORTS,
      RESOURCES.AWS_DEVOPS_AGENT_REPORTS,
      RESOURCES.AWS_SAGEMAKER_REPORTS,
      RESOURCES.AWS_QUICK_REPORTS,
      RESOURCES.AWS_KIRO_REPORTS,
      RESOURCES.AWS_COWORK_REPORTS,
      RESOURCES.AWS_AGENTS,
      RESOURCES.AWS_AGENTS_HEALTHOPS,
      RESOURCES.AWS_USER_MGT,
      RESOURCES.AWS_PERMISSION_SETS,
      RESOURCES.AZURE,
      RESOURCES.AZURE_REPORTS,
      RESOURCES.GCP,
      RESOURCES.GCP_REPORTS,
      RESOURCES.GCP_DATADOG_REPORTS,
      RESOURCES.OCI,
      RESOURCES.OCI_REPORTS,
    ],
    actions: [ACTIONS.VIEW, ACTIONS.CREATE],
  },
  // Dedicated group for the AWS LCCS (OutpostForge) module. Membership in
  // this group is what grants access to the LCCS pages; a plain authenticated
  // user without it is sent to Unauthorized.
  CES_LCCS_SLOTTING: {
    resources: [
      RESOURCES.LCCS,
    ],
    actions: [ACTIONS.VIEW, ACTIONS.CREATE, ACTIONS.UPDATE, ACTIONS.DELETE, ACTIONS.MANAGE],
  },
  CES_VIEWER: {
    resources: [
      RESOURCES.HOME,
      RESOURCES.AWS,
      RESOURCES.AWS_REPORTS,
      RESOURCES.AWS_ACCOUNTS_REPORTS,
      RESOURCES.AWS_EC2_REPORTS,
      RESOURCES.AWS_S3_REPORTS,
      RESOURCES.AWS_RDS_REPORTS,
      RESOURCES.AWS_EFS_REPORTS,
      RESOURCES.AWS_IAM_REPORTS,
      RESOURCES.AWS_AD_REPORTS,
      RESOURCES.AWS_COSTS_REPORTS,
      RESOURCES.AWS_HEALTH_REPORTS,
      RESOURCES.AWS_TA_REPORTS,
      RESOURCES.AWS_CO_REPORTS,
      RESOURCES.AWS_KPI_REPORTS,
      RESOURCES.AWS_CI_REPORTS,
      RESOURCES.AWS_LAMBDA_REPORTS,
      RESOURCES.AWS_COMPLIANCE_REPORTS,
      RESOURCES.AWS_DATADOG_CONSOLE_REPORTS,
      RESOURCES.AWS_NETWORKING_REPORTS,
      RESOURCES.AWS_EKS_REPORTS,
      RESOURCES.AWS_BEDROCK_AGENTS_REPORTS,
      RESOURCES.AWS_DEVOPS_AGENT_REPORTS,
      RESOURCES.AWS_SAGEMAKER_REPORTS,
      RESOURCES.AWS_QUICK_REPORTS,
      RESOURCES.AWS_AGENTS_HEALTHOPS,
      RESOURCES.AWS_QUICK_REPORTS,
      RESOURCES.AWS_KIRO_REPORTS,
      RESOURCES.AWS_COWORK_REPORTS,
      RESOURCES.AZURE,
      RESOURCES.AZURE_REPORTS,
      RESOURCES.GCP,
      RESOURCES.GCP_REPORTS,
      RESOURCES.GCP_DATADOG_REPORTS,
      RESOURCES.OCI,
      RESOURCES.OCI_REPORTS,
    ],
    actions: [ACTIONS.VIEW],
  },
};

export const AUTH_CONFIG = {
  // Configuración de AWS Amplify
  AMPLIFY: {
    REGION: "us-east-1",
    USER_POOL_ID: "us-east-1_QpQyfDXJK",
    USER_POOL_WEB_CLIENT_ID: "6n7nnvvof4ujj17nicbr8mi8g7",
  },

  // Configuración de rutas
  ROUTES: {
    LOGIN: "/login",
    LOGOUT: "/logout",
    UNAUTHORIZED: "/unauthorized",
    DEFAULT_REDIRECT: "/home",
    PUBLIC_ROUTES: ["/login", "/unauthorized"],
  },

  // Configuración de grupos de usuarios
  USER_GROUPS: {
    ADMIN: "CES_ADMIN",
    MANAGER: "CES_MANAGER",
    USER: "CES_USER",
    VIEWER: "CES_VIEWER",
    LCCS_SLOTTING: "CES_LCCS_SLOTTING",
    DEFAULT: "CES_USER",
  },

  // Configuración de validación
  VALIDATION: {
    MIN_PASSWORD_LENGTH: 8,
    MAX_PASSWORD_LENGTH: 128,
    MIN_USERNAME_LENGTH: 3,
    MAX_USERNAME_LENGTH: 50,
  },
};

export const AUTH_ERROR_MESSAGES = {
  INVALID_CREDENTIALS: "Usuario o contraseña incorrectos",
  NETWORK_ERROR: "Error de conexión. Intente nuevamente",
  SERVER_ERROR: "Error del servidor. Contacte al administrador",
  SESSION_EXPIRED: "Sesión expirada. Inicie sesión nuevamente",
  PASSWORD_CHANGE_REQUIRED: "Debe cambiar su contraseña para continuar",
  ACCOUNT_LOCKED: "Cuenta bloqueada. Contacte al administrador",
  TOO_MANY_ATTEMPTS: "Demasiados intentos. Intente más tarde",
};

export const AUTH_SUCCESS_MESSAGES = {
  LOGIN_SUCCESS: "Inicio de sesión exitoso",
  LOGOUT_SUCCESS: "Sesión cerrada exitosamente",
  PASSWORD_CHANGED: "Contraseña cambiada exitosamente",
};

// Funciones de utilidad para permisos mejoradas
export const hasPermission = (userGroups, resource, action = ACTIONS.VIEW) => {
  if (!userGroups || userGroups.length === 0) return false;
  
  return userGroups.some(group => {
    const rolePermissions = ROLE_PERMISSIONS[group];
    if (!rolePermissions) return false;
    
    return rolePermissions.resources.includes(resource) && 
           rolePermissions.actions.includes(action);
  });
};

export const hasResourceAccess = (userGroups, resource) => {
  return hasPermission(userGroups, resource, ACTIONS.VIEW);
};

export const canCreate = (userGroups, resource) => {
  return hasPermission(userGroups, resource, ACTIONS.CREATE);
};

export const canUpdate = (userGroups, resource) => {
  return hasPermission(userGroups, resource, ACTIONS.UPDATE);
};

export const canDelete = (userGroups, resource) => {
  return hasPermission(userGroups, resource, ACTIONS.DELETE);
};

export const canManage = (userGroups, resource) => {
  return hasPermission(userGroups, resource, ACTIONS.MANAGE);
};

export const getUserHighestRole = (userGroups) => {
  const roleHierarchy = [
    AUTH_CONFIG.USER_GROUPS.ADMIN, 
    AUTH_CONFIG.USER_GROUPS.MANAGER, 
    AUTH_CONFIG.USER_GROUPS.USER, 
    AUTH_CONFIG.USER_GROUPS.VIEWER
  ];
  return roleHierarchy.find(role => userGroups?.includes(role)) || null;
};

// Función para obtener todos los recursos a los que tiene acceso un usuario
export const getUserAccessibleResources = (userGroups) => {
  if (!userGroups || userGroups.length === 0) return [];
  
  const accessibleResources = new Set();
  
  userGroups.forEach(group => {
    const rolePermissions = ROLE_PERMISSIONS[group];
    if (rolePermissions) {
      rolePermissions.resources.forEach(resource => {
        accessibleResources.add(resource);
      });
    }
  });
  
  return Array.from(accessibleResources);
};

export default AUTH_CONFIG;
